import { useMemo, useRef, type ReactNode } from 'react';
import { highlight } from '@/components/highlight';
import { SearchSelect, type PickSource, type SearchOption } from '@/components/SearchSelect';
import { compare, matchCode, sameCode } from '@/components/codeMatch';
import { nextFocusable } from '@/components/focus';
import type { FieldSpan } from '@/components/ui';

export interface PickableStore {
  id: string;
  code: string;
  nameEn: string;
  nameUr?: string | null;
  farmCode?: string | null;
  farmName?: string | null;
  /** Distinct items with stock on hand — tells two same-named stores apart. */
  itemsInStock?: number;
}

/**
 * Picks a store with two linked boxes: its code and its name.
 *
 * People who know their stores by code type `ST-OKA`; everyone else types
 * "Okara". Whichever box is used, the other fills itself — both show the
 * same chosen store, so they can never disagree.
 *
 * Codes are unique, so a code typed in full is taken on Tab without opening
 * the list. Names are not (two farms can each have a "Feed Store"), so the
 * name list shows each store's code and farm, and a name shared by two
 * stores is never chosen for the user.
 */
export function StorePicker({
  label,
  value,
  onChange,
  stores,
  required,
  disabled,
  hint,
  placeholder,
  clearable,
  span,
  visibleLabels,
  labelClassName,
}: {
  /** "Receiving store" — the boxes become "Receiving store code" and "… name". */
  label: string;
  value: string;
  onChange: (storeId: string) => void;
  /** The stores to choose from; undefined while they are still loading. */
  stores: PickableStore[] | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  hint?: ReactNode;
  /** Shown in the name box when nothing is chosen, e.g. "All stores". */
  placeholder?: string | undefined;
  /** Erasing either box clears the choice, for a filter where nothing chosen
   *  means "all". */
  clearable?: boolean | undefined;
  span?: FieldSpan | undefined;
  /** Shorter words over the two boxes when `label` is long ("Store for item
   *  3"); `label` stays their name for screen readers. */
  visibleLabels?: { code: string; name: string } | undefined;
  labelClassName?: string | undefined;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const loading = stores === undefined;
  const total = stores?.length ?? 0;

  const { byId, codeOptions, nameOptions } = useMemo(() => {
    const list = stores ?? [];
    const byCode = [...list].sort((a, b) => compare(a.code, b.code));
    const byName = [...list].sort((a, b) => compare(a.nameEn, b.nameEn) || compare(a.code, b.code));
    return {
      byId: new Map(list.map((s) => [s.id, s])),
      // Each box searches what it is labelled with: the code box codes only,
      // the name box names (English or Urdu) and the farm they belong to.
      codeOptions: byCode.map((s): SearchOption => ({ value: s.id, label: s.code })),
      nameOptions: byName.map((s): SearchOption => ({
        value: s.id,
        label: s.nameEn,
        keywords: [s.nameUr, s.farmCode, s.farmName].filter(Boolean).join(' '),
      })),
    };
  }, [stores]);

  // A stale id (a draft naming a store since retired, or one outside the
  // farms now selected) shows as empty rather than as a raw id.
  const shown = value !== '' && byId.has(value) ? value : '';

  function pick(option: SearchOption | null) {
    onChange(option?.value ?? '');
  }

  // A code chosen from the keyboard has already filled the name, so move on
  // past it to whatever follows — the name box stays a click (or Shift+Tab)
  // away for anyone who wants to check it.
  function afterCodePick(_option: SearchOption, source: PickSource) {
    if (source !== 'enter' && source !== 'tab') return;
    setTimeout(() => {
      const nameInput = wrapRef.current?.querySelectorAll<HTMLInputElement>('input')[1];
      if (!nameInput) return;
      nextFocusable(nameInput)?.focus();
    }, 0);
  }

  // Both boxes open the same list, the full width of the pair, so a store
  // reads the same whichever box is searched: code, name, then farm. Only
  // the columns that box searches are highlighted.
  function row(option: SearchOption, query: string, by: 'code' | 'name') {
    const store = byId.get(option.value);
    if (!store) return option.label;
    // The farm's code, as the Stores list shows it.
    const farm = store.farmCode ?? store.farmName ?? 'Main store';
    const items = store.itemsInStock;
    const held = items === undefined ? null : items === 0 ? 'Empty' : `${items} ${items === 1 ? 'item' : 'items'}`;
    // What this box searches leads the row; the rest is one quiet line under
    // it, so no part of a long code or name can squeeze another out of view.
    const main = by === 'code' ? store.code : store.nameEn;
    const rest = [by === 'code' ? store.nameEn : store.code, farm, held].filter(Boolean) as string[];
    return (
      <span className="pickopt">
        <span className={`pickopt__main${by === 'code' ? ' pickopt__main--code' : ''}`} title={main}>
          {highlight(main, query)}
        </span>
        <span className="pickopt__sub" title={rest.join(' · ')}>
          {rest.map((part, i) => (
            <span key={i}>
              {i > 0 && ' · '}
              {by === 'name' && part === farm ? highlight(part, query) : part}
            </span>
          ))}
        </span>
      </span>
    );
  }

  // Two stores can share a name, even on one farm (a store added twice). The
  // code box always says which one was taken; this says that it matters.
  const chosen = shown ? byId.get(shown) : undefined;
  const twins = chosen
    ? (stores ?? []).filter((s) => s.id !== chosen.id && compare(s.nameEn, chosen.nameEn) === 0)
    : [];

  const header = (shown: number) =>
    shown === total ? `${total} ${total === 1 ? 'store' : 'stores'}` : `${shown} of ${total} stores`;
  const none = total === 0 ? 'No stores to choose from' : undefined;
  const off = disabled || loading;
  const spanCls = span === 2 ? ' formgrid__span-2' : span === 'full' ? ' formgrid__full' : '';

  return (
    <div ref={wrapRef} className={`storepicker${spanCls}`} role="group" aria-label={label}>
      <div className="storepicker__fields">
        <SearchSelect
          label={`${label} code`}
          // "Receiving store code" wraps in a box sized for a code; beside
          // "Receiving store name" a plain "Code" says the same.
          visibleLabel={visibleLabels?.code ?? (label === 'Store' ? undefined : 'Code')}
          labelClassName={labelClassName}
          value={shown}
          onChange={pick}
          options={codeOptions}
          required={required}
          disabled={off}
          placeholder={loading ? 'Loading…' : 'Code'}
          emptyText={none ?? 'No store with that code'}
          commitExact
          match={matchCode}
          isExact={sameCode}
          clearable={clearable}
          onPick={afterCodePick}
          renderOption={(o, q) => row(o, q, 'code')}
          listHeader={header}
        />
        <SearchSelect
          label={`${label} name`}
          visibleLabel={visibleLabels?.name}
          labelClassName={labelClassName}
          value={shown}
          onChange={pick}
          options={nameOptions}
          required={required}
          disabled={off}
          placeholder={loading ? 'Loading stores…' : (placeholder ?? 'Search by store name…')}
          emptyText={none ?? 'No store with that name'}
          commitExact
          clearable={clearable}
          clearButton
          renderOption={(o, q) => row(o, q, 'name')}
          listHeader={header}
        />
      </div>
      {twins.length > 0 && (
        <span className="settings__hint storepicker__twins" role="status">
          {twins.length === 1 ? 'Another store has' : `${twins.length} other stores have`} this name (
          {twins.map((t) => t.code).join(', ')}) — check the code.
        </span>
      )}
      {hint && <span className="settings__hint storepicker__hint">{hint}</span>}
    </div>
  );
}
